/* ══════════════════════════════════════════════════════════════════
   Was dem Kunden für die Rechnung fehlt — vor der Annahme gefragt (F8,
   04.10.2026; Befund AG-40). Ohne Abhängigkeiten, damit ein Test es ohne
   Browser prüft.

   Luca: „wenn dann noch Fragen offen sind wie fehlende USt-ID oder so, muss
   das abgefragt werden". Die API misst die Lücken als Kunde
   (`GET /vorgaenge/:id/annahme-luecken`, dieselbe Regel wie in der Akte),
   das Annahmeblatt fragt sie ab und schickt sie als `kunde_angaben` mit der
   Annahme. Die API füllt nur, was leer ist.
   ══════════════════════════════════════════════════════════════════ */

import { ustIdPruefen } from "@mrmachine/fach";

export type Lueckenfeld = "anschrift" | "telefon" | "ust_id" | "rechnung_email" | "ansprechpartner";

export type Annahmeluecken = {
  kunde: { nr: string; firma: string; interessent: boolean; typ: "privat" | "gewerblich" };
  felder: Lueckenfeld[];
  werte: {
    anschrift: { strasse: string; plz: string; ort: string; zusatz: string };
    telefon: string;
    ust_id: string;
    steuernummer: string;
    rechnung_email: string;
  };
};

/** Was im Blatt getippt wurde — dieselben Namen wie in `kunde_angaben`. */
export type Angabeneingabe = {
  strasse: string; plz: string; ort: string;
  telefon: string; ust_id: string; steuernummer: string; rechnung_email: string;
};

export const LEERE_EINGABE: Angabeneingabe = {
  strasse: "", plz: "", ort: "", telefon: "", ust_id: "", steuernummer: "", rechnung_email: "",
};

const FELDER: readonly Lueckenfeld[] = ["anschrift", "telefon", "ust_id", "rechnung_email", "ansprechpartner"];
const txt = (w: unknown) => (typeof w === "string" ? w : "");

/** Die Antwort lesen — `null`, wenn sie nicht passt. */
export function annahmelueckenAus(roh: unknown): Annahmeluecken | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  const k = (r["kunde"] ?? {}) as Record<string, unknown>;
  const w = (r["werte"] ?? {}) as Record<string, unknown>;
  const a = (w["anschrift"] ?? {}) as Record<string, unknown>;
  if (!txt(k["nr"])) return null;
  const felder = Array.isArray(r["felder"])
    ? (r["felder"] as unknown[]).filter((f): f is Lueckenfeld => FELDER.includes(f as Lueckenfeld))
    : [];
  return {
    kunde: {
      nr: txt(k["nr"]), firma: txt(k["firma"]), interessent: k["interessent"] === true,
      typ: k["typ"] === "privat" ? "privat" : "gewerblich",
    },
    felder,
    werte: {
      anschrift: { strasse: txt(a["strasse"]), plz: txt(a["plz"]), ort: txt(a["ort"]), zusatz: txt(a["zusatz"]) },
      telefon: txt(w["telefon"]),
      ust_id: txt(w["ust_id"]),
      steuernummer: txt(w["steuernummer"]),
      rechnung_email: txt(w["rechnung_email"]),
    },
  };
}

/** Welche Lücken das Blatt mit einem Feld füllen kann — der Ansprechpartner nicht (Akte). */
export function fuellbareLuecken(l: Annahmeluecken): Lueckenfeld[] {
  return l.felder.filter((f) => f !== "ansprechpartner");
}

const voll = (w: string) => w.trim() !== "";
const MAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** Ist die Lücke mit dem, was dasteht und was getippt wurde, gefüllt? */
export function lueckeGefuellt(feld: Lueckenfeld, l: Annahmeluecken, e: Angabeneingabe): boolean {
  const a = l.werte.anschrift;
  switch (feld) {
    case "anschrift":
      return (voll(a.strasse) || voll(e.strasse)) && (voll(a.plz) || voll(e.plz)) && (voll(a.ort) || voll(e.ort));
    case "telefon":
      return voll(e.telefon);
    case "ust_id":
      // Eine USt-IdNr., die die API abweisen würde, füllt nichts (08.10.2026).
      if (voll(e.ust_id)) return ustIdPruefen(e.ust_id).ok;
      return voll(e.steuernummer);
    case "rechnung_email":
      return MAIL.test(e.rechnung_email.trim());
    default:
      return false;
  }
}

/** Sind alle Lücken gefüllt, die das Blatt füllen kann? */
export function alleGefuellt(l: Annahmeluecken, e: Angabeneingabe): boolean {
  return fuellbareLuecken(l).every((f) => lueckeGefuellt(f, l, e));
}

/**
 * Was mit der Annahme hinausgeht — nur die Felder der Lücken, und nur, was
 * getippt wurde. `null`, wenn nichts.
 */
export function kundenangabenAus(l: Annahmeluecken, e: Angabeneingabe): Record<string, unknown> | null {
  const raus: Record<string, unknown> = {};
  const offen = new Set(fuellbareLuecken(l));
  if (offen.has("anschrift")) {
    const anschrift: Record<string, string> = {};
    for (const k of ["strasse", "plz", "ort"] as const) if (voll(e[k])) anschrift[k] = e[k].trim();
    if (Object.keys(anschrift).length) raus["anschrift"] = anschrift;
  }
  if (offen.has("telefon") && voll(e.telefon)) raus["telefon"] = e.telefon.trim();
  if (offen.has("ust_id")) {
    if (voll(e.ust_id)) raus["ust_id"] = e.ust_id.trim();
    if (voll(e.steuernummer)) raus["steuernummer"] = e.steuernummer.trim();
  }
  if (offen.has("rechnung_email") && voll(e.rechnung_email)) raus["rechnung_email"] = e.rechnung_email.trim();
  return Object.keys(raus).length ? raus : null;
}
